import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faBuilding, faFileAlt, faVenusMars, faClock,
  faCheckCircle, faExclamationTriangle, faPlus, faEdit, faTrash,
  faSyncAlt, faBriefcase, faGraduationCap, faMoneyBillWave,
  faFilter, faTimes, faEye, faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { PartenairesContext } from '../layout/CunstomPartenaires';

// ============================================================
// CACHE DEXIE (24h) avec possibilité de récupérer le cache périmé
// ============================================================
const db = new Dexie('OffresCacheDB');
db.version(1).stores({ offres: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedOffres(key, allowExpired = false) {
  try {
    const entry = await db.offres.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedOffres(key, data) {
  await db.offres.put({ key, data, timestamp: Date.now() });
}
async function getStaleOffres(key) {
  return getCachedOffres(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}offres/api_offres.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageOffres = () => {
  const context = useContext(PartenairesContext) || {};
  const { refreshKey, setSource } = context;

  // États
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ type_contrat: '', statut: '' });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // --- Modal ---
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    id_entreprise: '',
    description: '',
    sexe: '',
    moyen_age: '',
    competences: '',
    niveau_etude: '',
    experience_professionnelle: '',
    type_contrat: '',
    salaire: '',
    statut: 'Fermée'
  });
  const [formErrors, setFormErrors] = useState({});

  const queryParams = new URLSearchParams(window.location.search);
  const entrepriseId = queryParams.get('entreprise') ? parseInt(queryParams.get('entreprise')) : null;

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES OFFRES (avec dédoublonnage + annulation)
  // ============================================================
  const fetchOffres = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}_filters_${JSON.stringify(filters)}_ent_${entrepriseId}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setOffres(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedOffres(cacheKey);
          if (cached && isMounted.current) {
            setOffres(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
         const uniqueParam = new Date().getTime();
        let url = `${API_URL}?page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}&unique=${uniqueParam}`;
        if (entrepriseId) url += `&id_entreprise=${entrepriseId}`;
        if (filters.type_contrat) url += `&type_contrat=${encodeURIComponent(filters.type_contrat)}`;
        if (filters.statut) url += `&statut=${encodeURIComponent(filters.statut)}`;

        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleOffres(cacheKey);
          if (stale && isMounted.current) {
            setOffres(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchOffres(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleOffres(cacheKey);
          if (stale && isMounted.current) {
            setOffres(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setOffres(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedOffres(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleOffres(cacheKey);
        if (stale && isMounted.current) {
          setOffres(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, filters, entrepriseId, showToast, setSource]);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchOffres(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, fetchOffres, pagination.currentPage, searchTerm]);

  // ============================================================
  // GESTION DE LA RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchOffres(1, val, false);
    }, 400);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    // On recharge avec forceRefresh pour ne pas utiliser le cache du filtre précédent
    fetchOffres(1, searchTerm, true);
  };

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchOffres();
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // GESTION FORMULAIRE (inchangé)
  // ============================================================
  const resetForm = () => {
    setForm({
      id_entreprise: entrepriseId || '',
      description: '',
      sexe: '',
      moyen_age: '',
      competences: '',
      niveau_etude: '',
      experience_professionnelle: '',
      type_contrat: '',
      salaire: '',
      statut: 'Fermée'
    });
    setFormErrors({});
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.id_entreprise) errors.id_entreprise = 'Entreprise requise';
    if (!form.description.trim()) errors.description = 'Description requise';
    if (!form.competences.trim()) errors.competences = 'Compétences requises';
    if (!form.niveau_etude.trim()) errors.niveau_etude = 'Niveau d\'étude requis';
    if (!form.type_contrat) errors.type_contrat = 'Type de contrat requis';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (offre) => {
    setForm({
      id_entreprise: offre.id_entreprise?.toString() || '',
      description: offre.description || '',
      sexe: offre.sexe || '',
      moyen_age: offre.moyen_age?.toString() || '',
      competences: offre.competences || '',
      niveau_etude: offre.niveau_etude || '',
      experience_professionnelle: offre.experience_professionnelle?.toString() || '',
      type_contrat: offre.type_contrat || '',
      salaire: offre.salaire?.toString() || '',
      statut: offre.statut || 'Fermée'
    });
    setEditingId(offre.id);
    setFormErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      id_entreprise: parseInt(form.id_entreprise),
      description: form.description.trim(),
      sexe: form.sexe || null,
      moyen_age: form.moyen_age ? parseInt(form.moyen_age) : null,
      competences: form.competences.trim(),
      niveau_etude: form.niveau_etude.trim(),
      experience_professionnelle: form.experience_professionnelle ? parseInt(form.experience_professionnelle) : null,
      type_contrat: form.type_contrat.trim(),
      salaire: form.salaire ? parseFloat(form.salaire) : null,
      statut: form.statut
    };

    try {
      let res;
      if (editingId) {
        res = await fetch(`${API_URL}?id=${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Erreur');
      showToast(editingId ? 'Offre modifiée ✓' : 'Offre créée ✓', 'success');
      setShowModal(false);
      resetForm();
      fetchOffres(pagination.currentPage, searchTerm, true);
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ Supprimer définitivement cette offre ?')) return;
    try {
      const res = await fetch(`${API_URL}?id=${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Erreur');
      showToast('Offre supprimée ✓', 'success');
      fetchOffres(pagination.currentPage, searchTerm, true);
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  // ============================================================
  // RENDU AVEC STYLES RESPONSIFS OPTIMISÉS
  // ============================================================
  if (loading && offres.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des offres...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE OFFRES (amélioré + responsive)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        * { box-sizing: border-box; }
        .offre-list {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.icon-only {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .filter-group {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }
        .filter-group select {
          padding: 6px 12px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          color: var(--text-primary);
          outline: none;
          font-size: clamp(0.7rem, 1vw, 0.85rem);
          cursor: pointer;
          min-height: 40px;
        }
        .filter-group select:focus {
          border-color: var(--accent-blue);
        }
        .filter-group select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tbody tr {
          transition: background 0.15s;
        }
        .glass-table tbody tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .badge-statut {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: clamp(0.65rem, 0.8vw, 0.75rem);
          font-weight: 500;
        }
        .badge-statut.ouverte {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
        }
        .badge-statut.fermee {
          background: rgba(239, 68, 68, 0.15);
          color: #f87171;
        }
        .badge-statut.en-cours {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }

        .badge-contrat {
          display: inline-block;
          padding: 2px 10px;
          background: rgba(255,255,255,0.04);
          border-radius: 12px;
          font-size: clamp(0.65rem, 0.8vw, 0.75rem);
          color: var(--text-secondary);
          border: 1px solid rgba(255,255,255,0.04);
        }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          color: var(--text-primary);
        }

        /* --- Modales --- */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.25s ease;
          padding: 16px;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .modal-content {
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          padding: clamp(1.2rem, 2vw, 2rem);
          max-width: 560px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          color: var(--text-primary);
          box-shadow: 0 24px 60px rgba(0,0,0,0.8);
          animation: scaleIn 0.25s ease;
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .modal-content h3 {
          font-weight: 600;
          margin-bottom: 1.2rem;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: clamp(1.2rem, 2vw, 1.5rem);
        }
        .modal-content h3 .sub {
          font-weight: 400;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          color: var(--text-secondary);
        }

        .form-group {
          margin-bottom: 1rem;
        }
        .form-group label {
          display: block;
          margin-bottom: 4px;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          color: var(--text-secondary);
          font-weight: 500;
        }
        .form-group label .required {
          color: #f87171;
          margin-left: 2px;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px 14px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          color: var(--text-primary);
          outline: none;
          font-family: inherit;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
          transition: border 0.2s, box-shadow 0.2s;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .form-group textarea {
          min-height: 70px;
          resize: vertical;
        }
        .form-group .error-text {
          color: #f87171;
          font-size: clamp(0.7rem, 0.8vw, 0.8rem);
          margin-top: 4px;
        }
        .form-group input.error,
        .form-group select.error,
        .form-group textarea.error {
          border-color: #f87171;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.15);
        }
        .form-group select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .mt-3 { margin-top: clamp(1rem, 2vw, 1.5rem); }
        .mt-2 { margin-top: 0.5rem; }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.7rem, 0.9vw, 0.85rem); }
        .display-6 {
          font-size: clamp(1.6rem, 4vw, 2.2rem);
          font-weight: 700;
        }
        .m-0 { margin: 0; }
        .p-3 { padding: 1rem; }
        .p-0 { padding: 0; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }
        .me-2 { margin-right: 0.5rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-end { justify-content: flex-end; }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .offre-list { padding: 12px; }
          .glass-card { padding: 1rem; }

          .flex-between {
            flex-direction: column;
            align-items: stretch;
          }
          .flex-between > div:first-child {
            text-align: center;
          }

          .search-wrapper {
            max-width: 100%;
            min-width: unset;
          }

          .filter-group {
            flex-direction: column;
            width: 100%;
          }
          .filter-group select {
            width: 100%;
          }

          .glass-table th,
          .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table th:last-child,
          .glass-table td:last-child {
            min-width: 80px;
          }

          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .monetra-btn.icon-only {
            padding: 4px 8px;
            min-width: 32px;
          }

          .modal-overlay {
            padding: 8px;
          }
          .modal-content {
            padding: 1.2rem;
            max-width: 100%;
            border-radius: 20px;
          }
          .modal-content .form-group input,
          .modal-content .form-group select,
          .modal-content .form-group textarea {
            font-size: 0.9rem;
          }
          .modal-content .d-flex.justify-content-end {
            flex-direction: column;
            gap: 8px;
          }
          .modal-content .d-flex.justify-content-end button {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th,
          .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="offre-list">
        {/* Header avec badge source et boutons d'actualisation */}
        <div className="flex-between mb-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">💼 Offres d'emploi</h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              {entrepriseId ? 'Offres de cette entreprise' : 'Toutes les offres'}
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn"
              onClick={() => fetchOffres(pagination.currentPage, searchTerm, false)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> <span className="label">Actualiser</span>
            </button>
            <button
              className="monetra-btn"
              onClick={() => fetchOffres(pagination.currentPage, searchTerm, true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> <span className="label">Forcer</span>
            </button>
            <button className="monetra-btn primary" onClick={openAddModal} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faPlus} /> <span className="label">Nouvelle offre</span>
            </button>
          </div>
        </div>

        {/* Search + Filtres */}
        <div className="glass-card mb-3">
          <div className="flex-between">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
              <input
                type="text"
                placeholder="Rechercher (description, compétences...)"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div className="filter-group">
              <FontAwesomeIcon icon={faFilter} className="text-muted" />
              <select
                name="type_contrat"
                value={filters.type_contrat}
                onChange={handleFilterChange}
                disabled={loading || refreshing}
              >
                <option value="">Tous contrats</option>
                <option value="CDI">CDI</option>
                <option value="CDD">CDD</option>
                <option value="Stage">Stage</option>
                <option value="Alternance">Alternance</option>
                <option value="Freelance">Freelance</option>
              </select>
              <select
                name="statut"
                value={filters.statut}
                onChange={handleFilterChange}
                disabled={loading || refreshing}
              >
                <option value="">Tous statuts</option>
                <option value="Ouverte">Ouverte</option>
                <option value="Fermée">Fermée</option>
                <option value="En cours">En cours</option>
              </select>
            </div>
          </div>
          <div className="text-secondary small mt-2">
            <span className="fw-bold">{pagination?.totalRecords || 0}</span> offres
          </div>
        </div>

        {/* Table */}
        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Entreprise</th>
                  <th>Description</th>
                  <th>Compétences</th>
                  <th>Niveau</th>
                  <th>Contrat</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="8" className="text-center py-5"><div className="spinner-border text-primary" role="status" /></td></tr>
                ) : offres.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-5 text-secondary">Aucune offre trouvée.</td></tr>
                ) : (
                  offres.map(item => (
                    <tr key={item.id}>
                      <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                      <td><FontAwesomeIcon icon={faBuilding} className="me-2 text-muted" />{item.nomEntreprise || `ID: ${item.id_entreprise}`}</td>
                      <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.description}</td>
                      <td style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.competences}</td>
                      <td>{item.niveau_etude}</td>
                      <td><span className="badge-contrat">{item.type_contrat}</span></td>
                      <td>
                        <span className={`badge-statut ${item.statut?.toLowerCase().replace(' ', '-')}`}>
                          {item.statut || 'Fermée'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="monetra-btn icon-only"
                            onClick={() => openEditModal(item)}
                            disabled={loading || refreshing}
                            title="Modifier"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            className="monetra-btn icon-only danger"
                            onClick={() => handleDelete(item.id)}
                            disabled={loading || refreshing}
                            title="Supprimer"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 flex-between">
            <span className="text-secondary small">
              Page {pagination?.currentPage || 1} sur {pagination?.totalPages || 1}
            </span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchOffres(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchOffres(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Offre */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>
              {editingId ? '✏️ Modifier' : '➕ Ajouter'} une offre
              <span className="sub">{editingId ? `#${editingId}` : ''}</span>
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>ID Entreprise <span className="required">*</span></label>
                <input
                  type="number"
                  name="id_entreprise"
                  value={form.id_entreprise}
                  onChange={handleInputChange}
                  className={formErrors.id_entreprise ? 'error' : ''}
                  disabled={loading || refreshing}
                />
                {formErrors.id_entreprise && <div className="error-text">{formErrors.id_entreprise}</div>}
              </div>
              <div className="form-group">
                <label>Description <span className="required">*</span></label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleInputChange}
                  className={formErrors.description ? 'error' : ''}
                  disabled={loading || refreshing}
                />
                {formErrors.description && <div className="error-text">{formErrors.description}</div>}
              </div>
              <div className="form-group">
                <label>Sexe</label>
                <select
                  name="sexe"
                  value={form.sexe}
                  onChange={handleInputChange}
                  disabled={loading || refreshing}
                >
                  <option className="bg-dark" value="">Non spécifié</option>
                  <option className="bg-dark" value="Homme">Homme</option>
                  <option className="bg-dark" value="Femme">Femme</option>
                  <option className="bg-dark" value="Mixte">Mixte</option>
                </select>
              </div>
              <div className="form-group">
                <label>Âge moyen</label>
                <input
                  type="number"
                  name="moyen_age"
                  value={form.moyen_age}
                  onChange={handleInputChange}
                  placeholder="ex: 30"
                  disabled={loading || refreshing}
                />
              </div>
              <div className="form-group">
                <label>Compétences <span className="required">*</span></label>
                <input
                  type="text"
                  name="competences"
                  value={form.competences}
                  onChange={handleInputChange}
                  className={formErrors.competences ? 'error' : ''}
                  placeholder="ex: PHP, React, SQL"
                  disabled={loading || refreshing}
                />
                {formErrors.competences && <div className="error-text">{formErrors.competences}</div>}
              </div>
              <div className="form-group">
                <label>Niveau d'étude <span className="required">*</span></label>
                <input
                  type="text"
                  name="niveau_etude"
                  value={form.niveau_etude}
                  onChange={handleInputChange}
                  className={formErrors.niveau_etude ? 'error' : ''}
                  placeholder="ex: Bac+2, Master..."
                  disabled={loading || refreshing}
                />
                {formErrors.niveau_etude && <div className="error-text">{formErrors.niveau_etude}</div>}
              </div>
              <div className="form-group">
                <label>Expérience (années)</label>
                <input
                  type="number"
                  name="experience_professionnelle"
                  value={form.experience_professionnelle}
                  onChange={handleInputChange}
                  placeholder="ex: 3"
                  disabled={loading || refreshing}
                />
              </div>
              <div className="form-group">
                <label>Type de contrat <span className="required">*</span></label>
                <select
                  name="type_contrat"
                  value={form.type_contrat}
                  onChange={handleInputChange}
                  className={formErrors.type_contrat ? 'error' : ''}
                  disabled={loading || refreshing}
                >
                  <option className="bg-dark" value="">Sélectionner</option>
                  <option className="bg-dark" value="CDI">CDI</option>
                  <option className="bg-dark" value="CDD">CDD</option>
                  <option className="bg-dark" value="Stage">Stage</option>
                  <option className="bg-dark" value="Alternance">Alternance</option>
                  <option className="bg-dark" value="Freelance">Freelance</option>
                </select>
                {formErrors.type_contrat && <div className="error-text">{formErrors.type_contrat}</div>}
              </div>
              <div className="form-group">
                <label>Salaire ($)</label>
                <input
                  type="number"
                  step="0.01"
                  name="salaire"
                  value={form.salaire}
                  onChange={handleInputChange}
                  placeholder="ex: 35000"
                  disabled={loading || refreshing}
                />
              </div>
              <div className="form-group">
                <label>Statut</label>
                <select
                  name="statut"
                  value={form.statut}
                  onChange={handleInputChange}
                  disabled={loading || refreshing}
                >
                  <option className="bg-dark" value="Ouverte">Ouverte</option>
                  <option className="bg-dark" value="Fermée">Fermée</option>
                  <option className="bg-dark" value="En cours">En cours</option>
                </select>
              </div>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="monetra-btn"
                  onClick={() => setShowModal(false)}
                  disabled={loading || refreshing}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="monetra-btn primary"
                  disabled={loading || refreshing}
                >
                  {editingId ? 'Enregistrer' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageOffres;